/**
 * The idle session, shared with any panel that holds unsaved work.
 *
 * This is deliberately its own file, holding nothing but a React context.
 *
 * The gate that fills it (`components/IdleSessionGate.js`) has to import
 * `hanko-init` so it can revoke the session. A panel that only wants to
 * register a draft saver must not drag the whole auth SDK in behind it — doing
 * so pulled Hanko into the render tree of every panel under Bindings, and its
 * constructor needs browser APIs that a test environment does not have. The
 * tests failed on `BroadcastChannel is not defined`, which told nobody anything
 * about the panel being tested.
 *
 * So: context here, provider there. Panels import this file only.
 */

import { createContext, useContext, useEffect, useRef } from 'react';

export const IdleContext = createContext(null);

/**
 * Returns null when there is no gate above.
 *
 * That happens in three real cases: a panel rendered on its own in a test, a
 * local build with sign-in switched off, and any screen outside the gate. So
 * callers must cope with null rather than assume a session is there.
 */
export function useIdle() {
    return useContext(IdleContext);
}

/**
 * Register this panel's unsaved work with the idle clock.
 *
 *   useDraftSaver(() => {
 *     if (!dirty) return undefined;              // nothing to rescue
 *     const ok = save();
 *     return { saved: ok, where: 'local', label: 'Invitation message' };
 *   }, [dirty]);
 *
 * The saver runs at the 28-minute warning, two minutes before the lock.
 * Returning nothing means "I had nothing unsaved", which is not the same as
 * "I tried and failed". The lock screen tells those apart, so never return a
 * fake success.
 *
 * `@cloudsam/ui` exports the same hook, but importing it from there drags the
 * package index — and the whole component set with it — into every panel that
 * wants to save a draft. This is twelve lines. Keeping it local keeps the
 * panels' import graphs small, and keeps their tests from having to stand up an
 * app shell they never render.
 *
 * Does nothing when there is no gate above, which is the local no-sign-in case.
 */
export function useDraftSaver(saver, deps = []) {
    const idle = useIdle();
    const saverRef = useRef(saver);
    useEffect(() => { saverRef.current = saver; });

    const register = idle && idle.registerDraftSaver;
    useEffect(() => {
        if (!register) return undefined;
        // The registered function reads through the ref, so a panel can
        // re-render freely without unregistering and registering again.
        return register((...args) => saverRef.current(...args));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [register, ...deps]);
}

/**
 * Name what this tab currently has open, so its own lock screen can say what
 * it was instead of just that it happened.
 *
 *   useContextLabel(definition?.title);
 *
 * Pass `null` (or nothing) when there is nothing open worth naming.
 * Does nothing when there is no gate above, same as useDraftSaver.
 */
export function useContextLabel(label) {
    const idle = useIdle();
    const setLabel = idle && idle.setContextLabel;
    useEffect(() => {
        if (!setLabel) return undefined;
        setLabel(label || null);
        return undefined;
    }, [setLabel, label]);
}

export default IdleContext;
